import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { patientsApi } from '@/api/endpoints/patients.api';
import type { PatientProblem, CreateProblemRequest } from '@/types/patient.types';

const problemSchema = z.object({
  cidCode: z.string().optional(),
  description: z.string().min(1, 'Descrição é obrigatória'),
  onsetDate: z.string().optional(),
  notes: z.string().optional(),
});

type ProblemFormData = z.infer<typeof problemSchema>;

interface AddProblemDialogProps {
  patientId: string;
  onSuccess: (problem: PatientProblem) => void;
}

export function AddProblemDialog({ patientId, onSuccess }: AddProblemDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProblemFormData>({
    resolver: zodResolver(problemSchema),
    defaultValues: {
      cidCode: '',
      description: '',
      onsetDate: '',
      notes: '',
    },
  });

  const onSubmit = async (data: ProblemFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const request: CreateProblemRequest = {
        description: data.description,
        cidCode: data.cidCode || undefined,
        onsetDate: data.onsetDate || undefined,
        notes: data.notes || undefined,
      };

      const problem = await patientsApi.addProblem(patientId, request);
      onSuccess(problem);
      reset();
      setOpen(false);
    } catch (err) {
      console.error('Erro ao adicionar problema:', err);
      setError('Erro ao adicionar problema. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Problema
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Adicionar Problema</DialogTitle>
          <DialogDescription>
            Adicione um novo problema ou condição ao prontuário do paciente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cidCode">Código CID-10</Label>
              <Input
                id="cidCode"
                placeholder="Ex: J06.9"
                {...register('cidCode')}
              />
              {errors.cidCode && (
                <p className="text-sm text-destructive">{errors.cidCode.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="onsetDate">Data de Início</Label>
              <Input
                id="onsetDate"
                type="date"
                {...register('onsetDate')}
              />
              {errors.onsetDate && (
                <p className="text-sm text-destructive">{errors.onsetDate.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição *</Label>
            <Input
              id="description"
              placeholder="Descreva o problema ou condição"
              {...register('description')}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Observações</Label>
            <Textarea
              id="notes"
              placeholder="Observações adicionais..."
              {...register('notes')}
            />
            {errors.notes && (
              <p className="text-sm text-destructive">{errors.notes.message}</p>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
